/**
 * Pure helpers for merging chat messages by sequence number. Shared by the
 * open-thread hook (useRoomMessages) and the global room messages cache so
 * both sides dedupe/order identically.
 */

/**
 * Sort a list of messages by their sequence number (ascending).
 * @param {Array<Object>} list
 * @returns {Array<Object>}
 */
export const sortBySeq = (list) => [...list].sort((a, b) => a.seq - b.seq);

/**
 * Merge server messages into the current list, deduping by integer seq.
 * Optimistic (temp) messages are preserved; real messages always win on
 * matching seq.
 * @param {Array<Object>} existing
 * @param {Array<Object>} incoming
 * @returns {Array<Object>}
 */
export const mergeMessages = (existing, incoming) => {
  const bySeq = new Map();
  const optimistic = [];
  for (const msg of existing) {
    if (msg._optimistic) {
      optimistic.push(msg);
    } else {
      bySeq.set(msg.seq, msg);
    }
  }
  for (const msg of incoming) {
    bySeq.set(msg.seq, msg);
  }
  return sortBySeq([...bySeq.values(), ...optimistic]);
};

/**
 * Highest integer seq currently known (ignores optimistic fractional seqs).
 * @param {Array<Object>} list
 * @returns {number}
 */
export const maxRealSeq = (list) =>
  list.reduce(
    (max, msg) => (msg._optimistic ? max : Math.max(max, msg.seq || 0)),
    0
  );

/**
 * Lowest integer seq currently known (ignores optimistic messages).
 * @param {Array<Object>} list
 * @returns {number|undefined}
 */
export const minRealSeq = (list) => {
  const seqs = list.filter((m) => !m._optimistic).map((m) => m.seq);
  return seqs.length ? Math.min(...seqs) : undefined;
};

/**
 * Extract the message object from a realtime `message` event payload.
 * Publications arrive either flat ({ type: "message", ...msg }) or nested
 * ({ type: "message", message: {...} }). Returns null when the payload does
 * not carry a usable message (no id / non-integer seq) — callers must fall
 * back to a refetch in that case.
 * @param {Object} data Realtime publication payload.
 * @returns {Object|null}
 */
export const extractMessage = (data) => {
  const msg =
    data?.message && typeof data.message === "object" ? data.message : data;
  if (!msg || typeof msg !== "object") {
    return null;
  }
  if (msg.id == null || !Number.isInteger(msg.seq)) {
    return null;
  }
  return msg;
};
